import React from 'react';
import * as Yup from 'yup';
import {Row, Col} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {
  Input,
  Form,
  Checkbox,
  DropdownField,
  MainLoader,
  MaskedInput,
} from '@components';
import {
  GreenButton,
  BlackLinkButton,
  StyledH3,
  StyledLabel,
  LabelField,
} from '@styled';
import {PHONE_DIAL_CODE, USER_TITLES} from '@app/utils/constants';
import {useDispatch, useSelector} from 'react-redux';
import {get} from 'lodash';
import {setRedirect} from './redux';
import {sagaActions} from './redux/saga';

import TermsAndConditions from './TermsAndConditions';

const UserRegister = ({handleNext, token}) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [showGuidelines, setShowGuidelines] = React.useState(false);
  const [isAgreeTerms, setIsAgreeTerms] = React.useState(false);
  const isLoading = useSelector((state) => state.register.createUserLoading);
  const userDetails = useSelector((state) => state.register.userDetails);
  const redirectNext = useSelector((state) => state.register.redirect);

  const USER_TITLE_OPTIONS = React.useMemo(() => {
    return USER_TITLES.map((item) => ({
      label: item,
      value: item,
    }));
  }, [USER_TITLES]);

  React.useEffect(() => {
    if (redirectNext && redirectNext === 'UserRegister') {
      dispatch(setRedirect(''));
      handleNext(1);
    }
  }, [redirectNext]);

  const handleGuidelines = () => {
    setShowGuidelines(true);
  };

  const handleHideGuidelines = React.useCallback(() => {
    setShowGuidelines(false);
  }, [showGuidelines, isAgreeTerms]);

  const handleAgreeGuidelines = React.useCallback(() => {
    setShowGuidelines(false);
    setIsAgreeTerms(true);
  }, [showGuidelines, isAgreeTerms]);

  const handleSubmit = (values) => {
    dispatch({
      type: sagaActions.CREATE_USER,
      payload: {
        values: {...values},
        token,
        redirect: true,
      },
    });
  };

  return (
    <>
      {isLoading && (
        <MainLoader
          minHeight="100vh"
          spinnerColor="#479541"
          background="rgba(0,0,0,0.2)"
        />
      )}
      <div>
        <Form
          initialValues={{
            title:
              USER_TITLE_OPTIONS.find(
                (item) => item.value === get(userDetails, 'Title', '')
              ) || '',
            position: '',
            firstName: get(userDetails, 'Name', '').split(' ').shift(),
            middleName: get(userDetails, 'MiddleName', ''),
            lastName: get(userDetails, 'Name', '').split(' ').pop(),
            email: get(userDetails, 'Email', ''),
            phone: get(userDetails, 'Phone', '').replace(PHONE_DIAL_CODE, ''),
            password: '',
            confirmPassword: '',
            agreeTerms: [],
          }}
          validationSchema={Yup.object({
            position: Yup.string().required(
              t('register.user.required.position')
            ),
            firstName: Yup.string().required(
              t('register.user.required.firstName')
            ),
            lastName: Yup.string().required(
              t('register.user.required.lastName')
            ),
            email: Yup.string()
              .required(t('register.user.required.email'))
              .email(t('register.user.required.invalidEmail')),
            phone: Yup.string()
              .required(t('register.user.required.phoneNumber'))
              .test({
                name: 'is-valid-phone',
                message: t('register.user.required.invalidPhone'),
                test: (value) => {
                  let valid = value && value.indexOf('_') === -1;
                  if (valid) {
                    valid = Number(value) > 0;
                  }
                  return valid;
                },
              }),
            password: Yup.string()
              .required(t('register.user.required.password'))
              .min(5, t('register.user.required.invalidMinPassword'))
              .max(30, t('register.user.required.invalidMaxPassword')),
            confirmPassword: Yup.string()
              .oneOf(
                [Yup.ref('password'), null],
                t('register.user.required.passwordMustMatch')
              )
              .required(t('register.user.required.confirmPassword')),
            agreeTerms: Yup.array().min(
              1,
              t('register.user.required.agreeTerms')
            ),
          })}
          onSubmit={(values) => {
            handleSubmit(values);
          }}
        >
          <section className="content">
            <div className="container-fluid">
              <div className="card">
                <div className="card-body">
                  <StyledH3 className="mb-1 mt-0">
                    {t('register.user.userDetails')}
                  </StyledH3>
                  <Row className="mb-2">
                    <Col xs={12} lg={6}>
                      <DropdownField
                        id="Title"
                        name="title"
                        label={t('register.user.title')}
                        options={USER_TITLE_OPTIONS}
                        placeholder=""
                      />
                    </Col>
                    <Col xs={12} lg={6}>
                      <Input
                        type="text"
                        name="middleName"
                        id="MiddleName"
                        label={t('register.user.middleName')}
                        placeholder=""
                      />
                    </Col>
                  </Row>
                  <Row className="mb-2">
                    <Col xs={12} lg={6}>
                      <Input
                        type="text"
                        name="firstName"
                        id="FirstName"
                        label={t('register.user.firstName')}
                        placeholder=""
                        required
                      />
                    </Col>
                    <Col xs={12} lg={6}>
                      <Input
                        type="text"
                        name="lastName"
                        id="LastName"
                        label={t('register.user.lastName')}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>
                  <StyledH3 className="mb-1 mt-3">
                    {t('register.user.contactDetails')}
                  </StyledH3>
                  <Row className="mb-2">
                    <Col xs={12} lg={6}>
                      <Input
                        type="email"
                        name="email"
                        id="Email"
                        label={t('register.user.email')}
                        placeholder=""
                        required
                        disabled={get(userDetails, 'Email', '') ? true : false}
                      />
                    </Col>
                    <Col xs={12} lg={6}>
                      <Row noGutters>
                        <Col xs={3} lg={2} className="text-right mt-4">
                          <LabelField className="mb-1 mt-1 mr-2 text-nowrap">
                            {t('register.user.phoneDialCode')}
                          </LabelField>
                        </Col>
                        <Col xs={9} lg={10}>
                          <MaskedInput
                            mask="999999999"
                            name="phone"
                            id="phone"
                            label={t('register.user.phoneNumber')}
                            placeholder=""
                            required
                          />
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={12} lg={12}>
                      <Input
                        type="text"
                        name="position"
                        id="position"
                        label={t('register.user.position')}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>
                </div>
              </div>
              <div className="card">
                <div className="card-body">
                  <StyledH3 className="mb-1 mt-0">
                    {t('register.user.setYourPassword')}
                  </StyledH3>
                  <Row className="mb-2">
                    <Col xs={12} lg={6}>
                      <Input
                        type="password"
                        name="password"
                        id="Password"
                        label={t('register.user.password')}
                        placeholder=""
                        required
                      />
                    </Col>
                    <Col xs={12} lg={6}>
                      <Input
                        type="password"
                        name="confirmPassword"
                        id="ConfirmPassword"
                        label={t('register.user.confirmPassword')}
                        placeholder=""
                        required
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            </div>
            <div className="mx-2">
              <Row className="no-gutters">
                <Col>
                  <Checkbox
                    required
                    id="agreeTerms"
                    name="agreeTerms"
                    disabled
                    label={
                      <>
                        <StyledLabel htmlFor="agreeTerms" className="mr-1">
                          {t('register.user.agreeTermsLabel')}
                        </StyledLabel>
                        <BlackLinkButton
                          variant="link"
                          size="sm"
                          onClick={handleGuidelines}
                          style={{
                            padding: 0,
                            lineHeight: '15px',
                            fontSize: '13px',
                            verticalAlign: 'baseline',
                            margin: 0,
                          }}
                        >
                          {t('register.user.agreeTermsBtnLabel')}
                        </BlackLinkButton>
                      </>
                    }
                    feedback={t('register.user.agreeTermsFeedback')}
                    fieldValue="1"
                  />
                </Col>
              </Row>
              <Row className="mt-4">
                <Col>
                  <GreenButton type="submit" className="px-5">
                    {t('register.user.submitBtnLabel')}
                  </GreenButton>
                </Col>
              </Row>
            </div>
          </section>
          <TermsAndConditions
            show={showGuidelines}
            onHide={handleHideGuidelines}
            onAgree={handleAgreeGuidelines}
          />
        </Form>
      </div>
    </>
  );
};

export default UserRegister;
